// @flow
import React from "react";
import {Form, reduxForm} from "redux-form";
import ReduxFormTextField from "../../form/ReduxFormTextField";
import ReduxFormSelectField from "../../form/ReduxFormSelectField";
import {Button} from "reactstrap";
import * as _ from "lodash";
import FairConfig from "../../../model/fair/FairConfig";
import config from "../../../config/config";
import {withRouter} from "react-router-dom";
import type {History, Location} from "../../../types/ReactRouterTypes";
import * as queryString from "query-string";
import ReduxFormDatepicker from "../../form/ReduxFormDatepicker";
import ReduxFormCheckboxGroup, {ReduxFormCheckboxGroupItem} from "../../form/ReduxFormCheckboxGroup";
import FormSelectConstants , {validateReadableRefIdLength} from "../../constants/SearchFormConstants"
import './photo-ebadge-search-form.css';

export type PhotoEBadgeSearchCriteria = {
    registrationCode?: string,
    verifyStatus?: "Y" | "N",
    year?: string,
    projectCode?: Array<string>,
    fromSerial?: string,
    toSerial?: string,
    fromDate?: string,
    toDate?: string,
    sourceTypes?: Array<string>,
    visitorTypes?: Array<string>,
    fromModifiedDate?: string,
    toModifiedDate?: string,
    page?: number,
    pageItem?: number
};
export type PhotoEBadgeSearchFormStateProps = {
    fairConfigs: Array<FairConfig>,
    isLoading: boolean
}

export type PhotoEBadgeSearchFormDispatchProps = {
    onFormSubmit: (criteria: PhotoEBadgeSearchCriteria) => any,
    clearResult: () => any
}

type PhotoEBadgeSearchFormOwnProps = {
    handleSubmit: (any) => any,
    clearResult: () => any,
    history: History,
    location: Location
};

type PhotoEBadgeSearchFormProps =
    PhotoEBadgeSearchFormStateProps
    & PhotoEBadgeSearchFormDispatchProps
    & PhotoEBadgeSearchFormOwnProps;

const pushHistory = (history: History, location: Location) => {
    history.push(location);
};

const validateSerialLength = (value: ?string) => {
    const message = "Please input the first 6 digits of VMS ID only";
    if (!value) {
        return undefined;
    } else {
        if (value.length < 6 || /[^0-9]/i.test(value)) {
            return message;
        } else {
            return undefined;
        }
    }
};

const validateLength = (value: ?string) => {

    const message = `Please input 15 or 16 characters only.`;
    if (!value) {
        return undefined;
    } else {
        if (value.length < 15 || value.length > 16) {
            return message;
        } else {
            return undefined;
        }
    }
};

function PhotoEBadgeSearchForm(props: PhotoEBadgeSearchFormProps) {

    return (
        <Form id={"photo-ebadge-query-form"} onSubmit={props.handleSubmit((criteria: PhotoEBadgeSearchCriteria) => {

            criteria.page = 1;
            const location: Location = {
                pathname: props.location.pathname,
                search: "?" + queryString.stringify(criteria)
            };
            pushHistory(props.history, location);
            props.onFormSubmit(criteria);
        })} className={"mb-3"}>

            <div className="form-row">
                <div className="col-lg">

                    <ReduxFormTextField maxLength={16} type={"text"} name={"registrationCode"} id={"registrationCode"}
                                        warn={validateLength} validate={validateLength} label={"VMS Registration Number"}
                                        placeholder={"Please input the Registration Code"}/>

                    <ReduxFormTextField maxLength={12} type={"text"} name={"readableRefId"} id={"readableRefId"}
                                        label={"readableRefId"} warn={validateReadableRefIdLength} validate={validateReadableRefIdLength}
                                        placeholder={"Please input the readableRefId"}/>

                    <ReduxFormSelectField type={"select"} name={"verifyStatus"} id={"verification-status"} label={"Photo Verification Status"}
                                          placeholder={"Please Select the Fair"}>
                        <option value={""}>Please Select:</option>
                        <option value={"Y"}>Validated</option>
                        <option value={"N"}>Invalidated</option>
                    </ReduxFormSelectField>

                    <div className={"row"}>
                        <div className={"col-md-6"}>
                            <ReduxFormTextField length={6} maxLength={6} type={"text"} name={"fromSerial"} id={"fromSerial"} placeholder={"From"}
                                                label={"VMS Serial Number (First 6 Digits)"} warn={validateSerialLength}
                                                validate={validateSerialLength}/>
                        </div>
                        <div className={"col-md-6"}>
                            <ReduxFormTextField length={6} maxLength={6} type={"text"} name={"toSerial"} id={"toSerial"} placeholder={"To"}
                                                label={"VMS Serial Number (First 6 Digits)"} warn={validateSerialLength}
                                                validate={validateSerialLength}/>
                        </div>
                    </div>

                    <div className={"row"}>
                        <div className={"col-md-6"}>
                            <ReduxFormCheckboxGroup label={"Source Type"} name={"sourceTypes"} id={"sourceTypes"} className={"scrollableCheckboxGroup"}>
                                {FormSelectConstants.sourceTypes.map((item, index) => (
                                    <ReduxFormCheckboxGroupItem
                                        key={index}
                                        label={item.label}
                                        value={item.value}
                                        selectAll={item.selectAll || false}
                                    />
                                ))}
                            </ReduxFormCheckboxGroup>
                        </div>
                        <div className={"col-md-6"}>
                            <ReduxFormCheckboxGroup name={"visitorTypes"} label={"Visitor Type"} id={"visitorTypes"} className={"scrollableCheckboxGroup"}>
                                {FormSelectConstants.visitorType.map((item, index) => (
                                    <ReduxFormCheckboxGroupItem
                                        key={index}
                                        label={item.label}
                                        value={item.value}
                                        selectAll={item.selectAll || false}
                                    />
                                ))}
                            </ReduxFormCheckboxGroup>
                        </div>
                    </div>
                </div>
                <div className="col-lg">

                    <ReduxFormSelectField type={"select"} name={"year"} id={"year"} label={"Year"} placeholder={"Please Select the Fair"}>
                        <option value={""}>Please Select:</option>
                        {
                            _.chain(props.fairConfigs).map((fairConfig: FairConfig): number => +fairConfig.year)
                                .uniq()
                                .orderBy([], ['desc'])
                                .map((year: string, i: number) => <option key={i} value={year}>{year}</option>).value()

                        }
                    </ReduxFormSelectField>

                    <ReduxFormCheckboxGroup
                        label={"Fair"}
                        id={"projectCode"}
                        name={"projectCode"}>
                        {
                            _.chain(props.fairConfigs)
                                .filter((fairConfig: FairConfig): boolean => fairConfig.ebadgeMode==='Photo')
                                .map((fairConfig: FairConfig) => ({
                                    projectNumber: fairConfig.projectNumber,
                                    fairNames: fairConfig.fairNames,
                                    fairCode: fairConfig.fairCode
                                }))
                                .uniqBy((fc) => "" + fc.projectNumber)
                                .map((fc, i: number) => <ReduxFormCheckboxGroupItem key={i}
                                                                                    label={_.get(fc, "fairNames[0].name", fc.fairCode)}
                                                                value={`${fc.projectNumber}`}/>)
                                .value()
                        }
                    </ReduxFormCheckboxGroup>

                    <div className={"row"}>
                        <div className={"col-md-6"}>
                            <ReduxFormDatepicker label={"Photo Upload Date"} name={"fromDate"} id={"fromDate"} placeholder={"From"}/>
                        </div>
                        <div className={"col-md-6"}>
                            <ReduxFormDatepicker label={"Photo Upload Date"} name={"toDate"} id={"toDate"} placeholder={"To"}/>
                        </div>
                    </div>
                    <div className={"row"}>
                        <div className={"col-md-6"}>
                            <ReduxFormDatepicker label={"Photo Modified Date"} name={"fromModifiedDate"} id={"fromModifiedDate"} placeholder={"From"}/>
                        </div>
                        <div className={"col-md-6"}>
                            <ReduxFormDatepicker label={"Photo Modified Date"} name={"toModifiedDate"} id={"toModifiedDate"} placeholder={"To"}/>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <Button disabled={props.isLoading} color={"primary"}>
                    <span>Search</span>
                </Button>{' '}
                {config.enableResetButton && <Button disabled={props.isLoading} color={"warning"} onClick={props.clearResult}>
                    <span>Reset</span>
                </Button>}
            </div>
        </Form>
    )
}

export default reduxForm({form: "PhotoEBadgeSearchForm"})(withRouter(PhotoEBadgeSearchForm));


// WEBPACK FOOTER //
// ./src/components/ebadge/photo/PhotoEBadgeSearchForm.js